import * as _ from 'lodash';
import { compact, flatMap, map } from 'remeda';
import { v4 as uuidv4 } from 'uuid';

import {
  date as commonDate,
  utils as commonUtils,
  typeguards as commonTypeguards,
} from '@finley-technologies/common';
import { CreditAgreement } from '@finley-technologies/finley-types';

import { Maybe } from 'types/dashboard-core-gqlTypes.generated';

export {
  Bank,
  CreditAgreement,
  DbTable,
  Report,
} from '@finley-technologies/finley-types';

export const METADATA_DISPLAY_FIELDS: (keyof CreditAgreement.DomainEntity.CreditAgreementMetadata)[] = [
  'id',
  'creditFacilityId',
  'version',
  'effectiveFrom',
  'effectiveTo',
  's3Path',
  '_schemaVersion_',
  '_note_',
];

export const defaultDetailWidth = 56;

export const defaultDetailFontSize = 16;

export const isPropertyContainerType = (
  test: any,
): test is CreditAgreement.DomainEntity.PropertyContainer => {
  return (
    test &&
    _.includes(
      _.values(CreditAgreement.DomainEntity.PropertyContainerType),
      test,
    )
  );
};

export const isPropertyContainer = (
  test: any,
): test is CreditAgreement.DomainEntity.PropertyContainer => {
  return test && test.type && isPropertyContainerType(test.type);
};

export const isPropertyContainerTypeObjectValues = (
  test: any,
): test is Record<string, CreditAgreement.DomainEntity.PropertyContainer> => {
  if (!test || typeof test !== 'object') {
    return false;
  }

  return _.every(test, (testItem) => {
    if (testItem) {
      return isPropertyContainerType(testItem.type);
    }
    return false;
  });
};

export const isPropertyType = (
  test: any,
): test is CreditAgreement.DomainEntity.PropertyType => {
  return (
    test &&
    _.includes(_.values(CreditAgreement.DomainEntity.PropertyType), test)
  );
};

export const isProperty = (
  test: any,
): test is CreditAgreement.DomainEntity.PropertyType => {
  return test && test.type && isPropertyType(test.type);
};

export const isPropertyTypeObjectValues = (
  test: any,
): test is Record<string, CreditAgreement.DomainEntity.PropertyType> => {
  if (!test || typeof test !== 'object') {
    return false;
  }

  return _.every(test, (testItem) => {
    if (testItem) {
      return isPropertyType(testItem.type);
    }
    return false;
  });
};

export const getBooleanSymbol = (value: Maybe<boolean>) => {
  if (_.isNil(value)) {
    return '-';
  }
  if (value) {
    return '\u2713'; // checkmark
  }
  return '\u2717'; // x
};

// returns the last segment of a base property key
// eg. 'borrowingBaseMechanics.calcAgentMechanics.loanProductMechanics[idx].receivableAdvanceRate' -> 'Receivable Advance Rate'
export const formatBasePropertyKey = (value: string) => {
  const basePropertyKeySegments = value.split('.');

  return commonUtils.toTitleCase(_.last(basePropertyKeySegments) ?? value);
};

export const parseCreditAgreementFullArtifact = (
  test?: string,
): Maybe<CreditAgreement.DomainEntity.CreditAgreementFullArtifact> => {
  if (!test) {
    return null;
  }
  const creditAgreementFullArtifact = JSON.parse(test);

  const validate = commonTypeguards.creditAgreementFullArtifact.validate(test);
  if (validate !== true) {
    throw new Error(JSON.stringify(validate.errors));
  }
  return creditAgreementFullArtifact;
};

interface MaybeHasIterationKey {
  _internalIterationKey_?: () => string;
}

// Iteration keys can optionally be attached to any object type.
const maybeHasIterationKey = (_x: object): _x is MaybeHasIterationKey => true;

/**
 * Key for rendering this object with a React component in a loop.
 *
 * Survives copying, as long as copying happens after the first call to
 * getIterationKey, i.e.
 *
 * ```
 * const x = {};
 * getIterationKey(x);
 * getIterationKey(x) === getIterationKey(clone(x)) // true
 * ```
 *
 * This is not always desirable and can lead to bugs. See
 * {@link clearIterationKey}.
 *
 * Does not appear in serialized JSON, i.e.
 * ```
 * const x = {};
 * getIterationKey(x);
 * JSON.stringify(x) === '{}' // true
 * ```
 */
export const getIterationKey = (x: object): string => {
  if (maybeHasIterationKey(x) && _.isFunction(x._internalIterationKey_)) {
    return x._internalIterationKey_();
  }
  const id = uuidv4();
  Object.defineProperty(x, '_internalIterationKey_', {
    value: () => id, // Using a function intead of a string value hides the property from JSON.stringify().
    enumerable: true, // Preserve this property when making copies.
    configurable: true, // Allow the property to be `delete`-ed.
    writable: false,
  });
  return id;
};

/**
 * Clears iteration key on an object. Typical use case:
 *
 * ```
 * getIterationKey(x);
 * const a = cloneDeep(x);
 * clearIterationKey(a);
 * ```
 *
 * where we want `a` to have the same initial values as `x`, but we want them
 * rendered as two separate entities in the UI. Failure to call
 * clearIterationKey() will cause the UI to become out of sync with the
 * underlying state.
 *
 * We would not call clearIterationKey() if `a` is to become the latest version
 * of `x`, and will be replacing `x` in the UI.
 *
 * It is not necessary to call clearIterationKey() before calling
 * JSON.stringify().
 */
export const clearIterationKey = (x: object) => {
  if (maybeHasIterationKey(x)) {
    // eslint-disable-next-line no-param-reassign
    delete x._internalIterationKey_;
  }
};

export const getCreditAgreementFullArtifactLabel = (
  creditAgreementDocument: CreditAgreement.DomainEntity.CreditAgreementFullArtifact,
) => {
  const { version, effectiveFrom } = creditAgreementDocument;
  return `Version ${version}\n(Effective from ${commonDate.epochToLocaleDateString(
    effectiveFrom,
  )})`;
};

export const getEligibilityKeys = (
  loanProductMechanics: CreditAgreement.DomainEntity.LoanProductMechanicsWithEntities[],
): string[] =>
  compact(
    flatMap(loanProductMechanics, ({ eligibilityTests }) =>
      map(
        eligibilityTests,
        ({ _config_ }) => _config_?.eligibilityTestConfig?.propertyKey,
      ),
    ),
  );

export const getConcentrationLimitKeys = ({
  loanProductMechanics,
  multiLoanProductConcentrationLimits,
}: {
  loanProductMechanics: CreditAgreement.DomainEntity.LoanProductMechanicsWithEntities[];
  multiLoanProductConcentrationLimits: CreditAgreement.DomainEntity.MultiLoanProductConcentrationLimitWithEntities[];
}): string[] => {
  const concentrationLimitKeys = flatMap(
    loanProductMechanics,
    ({ concentrationLimits }) =>
      map(
        concentrationLimits,
        ({ _config_ }) => _config_?.concentrationLimitConfig?.propertyKey,
      ),
  );

  const multiLoanProductConcentrationLimitKeys = map(
    multiLoanProductConcentrationLimits,
    (multiLoanProductConcentrationLimit) =>
      multiLoanProductConcentrationLimit._config_.concentrationLimitConfig
        ?.propertyKey,
  );

  return compact([
    ...concentrationLimitKeys,
    ...multiLoanProductConcentrationLimitKeys,
  ]);
};
